import { useState, useEffect } from "react";
import ToggleSwitch from "../../../../../shared/ToggleSwitch";
import SelectField from "../../../../common/SelectField";
import {
    ACCUMULATOR_PLAN_YEAR_OPTIONS,
    ACCUMULATOR_NETWORK_LABEL_OPTIONS,
    formatAccumulatorCurrency,
    sanitizeAccumulatorNumeric,
    hasAccumulatorPositiveValue,
} from "../../../../../../utils/planManager/planConfigurationUtils.js";

/**
 * Accumulator table rows, each mapping a label to the in/out-of-network amount
 * field names used in the form state.
 * @readonly
 * @type {ReadonlyArray<{ label: string, inField: string, outField: string }>}
 */
export const ACCUMULATOR_ROWS = Object.freeze([
    {
        label: "Individual Deductible",
        inField: "inNetworkDeductiblePatientAmount",
        outField: "outOfNetworkDeductiblePatientAmount",
    },
    {
        label: "Family Deductible",
        inField: "inNetworkDeductibleFamilyAmount",
        outField: "outOfNetworkDeductibleFamilyAmount",
    },
    {
        label: "Individual Out-of-pocket Max",
        inField: "inNetworkOutOfPocketPatientMaxAmount",
        outField: "outOfNetworkOutOfPocketPatientMaxAmount",
    },
    {
        label: "Family Out-of-pocket Max",
        inField: "inNetworkOutOfPocketFamilyMaxAmount",
        outField: "outOfNetworkOutOfPocketFamilyMaxAmount",
    },
]);


/**
 * Accumulator detail-select option lists, keyed by field name.
 * @readonly
 * @type {Readonly<Record<string, ReadonlyArray<string>>>}
 */
export const ACCUMULATOR_SELECT_OPTIONS = Object.freeze({
    category: Object.freeze(["M", "V", "D", "RX"]),
    planYear: ACCUMULATOR_PLAN_YEAR_OPTIONS,
    deductibleType: Object.freeze(["Embedded", "Aggregate"]),
    ...ACCUMULATOR_NETWORK_LABEL_OPTIONS,
});

import "./Accumulators.scss";

/**
 * Options for a network-label select, keeping a saved value that isn't in the
 * standard list so it still displays.
 * @param {"inNetworkLabel" | "outOfNetworkLabel"} field
 * @param {string} currentValue
 * @returns {ReadonlyArray<string>}
 */
const getNetworkLabelOptions = (field, currentValue) => {
    const options = ACCUMULATOR_SELECT_OPTIONS[field];
    return currentValue && !options.includes(currentValue)
        ? [...options, currentValue]
        : options;
};

/**
 * A single amount cell: a text input paired with an enable/disable toggle.
 *
 * @param {object} props
 * @param {string} props.field - Amount field name in the accumulators slice.
 * @param {string} props.value - Current amount value.
 * @param {boolean} props.isEnabled - Whether the amount is enabled.
 * @param {string} props.ariaLabel - Accessible label for the input.
 * @param {(field: string, value: *) => void} props.onFieldChange - Emits a change.
 * @returns {import('react').JSX.Element}
 */
const AccumulatorAmountCell = ({
    field,
    value,
    isEnabled,
    ariaLabel,
    onFieldChange,
}) => {
    const [displayValue, setDisplayValue] = useState(() =>
        formatAccumulatorCurrency(value),
    );
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (!isFocused) {
            setDisplayValue(formatAccumulatorCurrency(value));
        }
    }, [value, isFocused]);

    const hasValue = hasAccumulatorPositiveValue(value);

    const handleAmountChange = (event) => {
        const rawInput = event.target.value;
        const sanitized = sanitizeAccumulatorNumeric(rawInput);
        setDisplayValue(sanitized);
        onFieldChange(field, sanitized);

        const nextHasValue = hasAccumulatorPositiveValue(sanitized);
        if (nextHasValue && !isEnabled) {
            onFieldChange(`${field}Enabled`, true);
        } else if (!nextHasValue && isEnabled) {
            onFieldChange(`${field}Enabled`, false);
        }
    };

    const handleFocus = () => {
        setIsFocused(true);
        setDisplayValue(sanitizeAccumulatorNumeric(value));
    };

    const handleBlur = () => {
        setIsFocused(false);
        const formatted = formatAccumulatorCurrency(value);
        setDisplayValue(formatted);
        onFieldChange(field, formatted);
    };

    return (
        <div className="plan-setup-accumulators__amount">
            <input
                type="text"
                value={displayValue}
                aria-label={ariaLabel}
                onChange={handleAmountChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
            <ToggleSwitch
                checked={Boolean(isEnabled)}
                disabled={!hasValue}
                onToggle={(nextEnabled) =>
                    onFieldChange(`${field}Enabled`, nextEnabled)
                }
            />
        </div>
    );
};

/**
 * Accumulators step (Javelina only — the Page hides it for HRP). Presentational
 * — renders the detail selects, the in/out-of-network accumulator table with an
 * amount input and toggle per cell, and a Reset control. Owns no state.
 *
 * @param {object} props
 * @param {Object} props.values - The Accumulators slice of the wizard form state.
 * @param {Record<string, string>} [props.errors] - Field-level validation errors.
 * @param {(field: string, value: *) => void} props.onFieldChange - Emits a single field change.
 * @param {() => void} props.onReset - Emits a request to reset the accumulators to defaults.
 * @returns {import('react').JSX.Element}
 */
const Accumulators = ({ values, errors = {}, onFieldChange, onReset }) => {
    return (
        <div className="plan-setup-step plan-setup-accumulators">
            <SelectField
                id="accum-category"
                label="Category"
                value={values.category}
                error={errors.category}
                onChange={(value) => onFieldChange("category", value)}
                placeholderOption="Select a category"
                options={ACCUMULATOR_SELECT_OPTIONS.category.map((option) => ({
                    value: option,
                    label: option,
                }))}
            />
            <SelectField
                id="accum-plan-year"
                label="Plan Year"
                value={values.planYear}
                error={errors.planYear}
                onChange={(value) => onFieldChange("planYear", value)}
                placeholderOption="Select a plan year"
                options={ACCUMULATOR_SELECT_OPTIONS.planYear.map((option) => ({
                    value: option,
                    label: option,
                }))}
            />
            <SelectField
                id="accum-deductible-type"
                label="Deductible Type"
                value={values.deductibleType}
                error={errors.deductibleType}
                onChange={(value) => onFieldChange("deductibleType", value)}
                placeholderOption="Select a deductible type"
                options={ACCUMULATOR_SELECT_OPTIONS.deductibleType.map(
                    (option) => ({
                        value: option,
                        label: option,
                    }),
                )}
            />

            <p className="plan-setup-accumulators__section-title">
                Accumulators
            </p>

            {(errors.inNetworkLabel || errors.outOfNetworkLabel) && (
                <p className="plan-setup-accumulators__error" role="alert">
                    {[errors.inNetworkLabel, errors.outOfNetworkLabel]
                        .filter(Boolean)
                        .join(" ")}
                </p>
            )}

            <table className="plan-setup-accumulators__table">
                <thead>
                    <tr>
                        <th scope="col" />
                        <th scope="col">
                            <span className="plan-setup-accumulators__col-title">
                                In-Network
                            </span>
                            <select
                                aria-label="In-Network tier label"
                                value={values.inNetworkLabel}
                                onChange={(event) =>
                                    onFieldChange(
                                        "inNetworkLabel",
                                        event.target.value,
                                    )
                                }
                            >
                                <option value="">Select label</option>
                                {getNetworkLabelOptions(
                                    "inNetworkLabel",
                                    values.inNetworkLabel,
                                ).map(
                                    (option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ),
                                )}
                            </select>
                        </th>
                        <th scope="col">
                            <span className="plan-setup-accumulators__col-title">
                                Out-of-Network
                            </span>
                            <select
                                aria-label="Out-of-Network tier label"
                                value={values.outOfNetworkLabel}
                                onChange={(event) =>
                                    onFieldChange(
                                        "outOfNetworkLabel",
                                        event.target.value,
                                    )
                                }
                            >
                                <option value="">Select label</option>
                                {getNetworkLabelOptions(
                                    "outOfNetworkLabel",
                                    values.outOfNetworkLabel,
                                ).map(
                                    (option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ),
                                )}
                            </select>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {ACCUMULATOR_ROWS.map((row) => (
                        <tr key={row.label}>
                            <td>{row.label}</td>
                            <td>
                                <AccumulatorAmountCell
                                    field={row.inField}
                                    value={values[row.inField]}
                                    isEnabled={values[`${row.inField}Enabled`]}
                                    ariaLabel={`${row.label} (in-network)`}
                                    onFieldChange={onFieldChange}
                                />
                            </td>
                            <td>
                                <AccumulatorAmountCell
                                    field={row.outField}
                                    value={values[row.outField]}
                                    isEnabled={values[`${row.outField}Enabled`]}
                                    ariaLabel={`${row.label} (out-of-network)`}
                                    onFieldChange={onFieldChange}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <button
                type="button"
                className="plan-setup-accumulators__reset"
                onClick={onReset}
            >
                Reset
            </button>
        </div>
    );
};

export default Accumulators;
